import { useMemo, useState } from 'react';

import { Badge, Button, Card, cn, focusRing, Skeleton, StatusPill } from '@/design-system';
import { groupMessages, type ClientMessage } from '@/entities/notification';
import type { TenantSettings } from '@/entities/tenant';
import { ErrorState } from '@/shared/ui';

import { clientMessagesCopy as copy } from '../copy';
import { useClientMessages, useTenantSettings } from '../hooks';
import { INVOICE_WITH_REPORT_ID, REMINDERS_ID } from './AutomationCard';
import { ClientMessageEditor } from './ClientMessageEditor';
import { InvoiceMessageSetting } from './InvoiceMessageSetting';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

/** De berichten in de volgorde waarin een klant ze krijgt; alleen deze toont de kaart. */
const MESSAGE_ORDER = Object.keys(copy.when);

export interface ClientMessagesCardProps {
    companyName: string;
    /** Alleen voor tests: hoe lang het voorbeeld wacht na het typen. */
    previewDelayMs?: number;
}

/**
 * Berichten aan klanten (scherm 11, rechterkolom, alleen voor wie instellingen
 * beheert).
 *
 * Elk automatisch bericht aan een klant op één regel: wanneer het uitgaat, via
 * welke kanalen, en of het de standaardtekst van Smartkeur is of een eigen tekst
 * van het bedrijf. Waar een instelling het bericht aan- of uitzet (de
 * herinnering aan de afspraak, de factuur die vanzelf met het rapport meegaat)
 * staat de stand erbij, met een link naar de schakelaar onder Automatisering op
 * het tabblad Rapport: elke schakelaar staat op één plek. Bij het bericht met
 * rapport en factuur staat ook het standaard eigen bericht: daar begint de
 * verstuurdialoog mee. **Bewerken** opent de editor met voorbeeld.
 */
export function ClientMessagesCard({ companyName, previewDelayMs }: ClientMessagesCardProps) {
    const messages = useClientMessages(true);
    const settings = useTenantSettings();
    const [openKey, setOpenKey] = useState<string | null>(null);

    const ordered = useMemo(() => {
        const byKey = new Map(
            groupMessages(messages.data ?? []).map((message) => [message.key, message]),
        );

        return MESSAGE_ORDER.map((key) => byKey.get(key)).filter(
            (message): message is ClientMessage => message !== undefined,
        );
    }, [messages.data]);

    const open = ordered.find((message) => message.key === openKey) ?? null;

    return (
        <Card padding="lg" as="section" aria-labelledby="client-messages-title">
            <h2
                id="client-messages-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {copy.title}
            </h2>
            <p className={`m-0 mt-1 mb-2 ${SMALL}`}>{copy.intro}</p>

            {messages.isPending ? (
                <div aria-busy="true">
                    <Skeleton height={14} label={copy.loading} className="mt-3" />
                    <Skeleton width="70%" height={14} className="mt-2" />
                    <Skeleton width="85%" height={14} className="mt-2" />
                </div>
            ) : messages.isError ? (
                <ErrorState
                    error={messages.error}
                    title={copy.title}
                    onRetry={() => void messages.refetch()}
                />
            ) : (
                <ul className="m-0 list-none p-0">
                    {ordered.map((message) => (
                        <li
                            key={message.key}
                            className="border-b border-[var(--border-subtle-2)] py-[11px] last:border-b-0"
                        >
                            <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                                <div className="min-w-0 flex-[1_1_200px]">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                                            {message.label}
                                        </span>
                                        <StatusPill
                                            tone={message.customised ? 'accent' : 'neutral'}
                                        >
                                            {message.customised ? copy.customised : copy.standard}
                                        </StatusPill>
                                    </div>
                                    <p className={`m-0 mt-[3px] ${SMALL}`}>
                                        {copy.when[message.key]}
                                    </p>
                                    <ul
                                        aria-label={copy.channelsLabel}
                                        className="m-0 mt-[6px] flex list-none flex-wrap gap-[6px] p-0"
                                    >
                                        {message.channels.map((channel) => (
                                            <li key={channel.channel}>
                                                <Badge variant="text" tone="neutral">
                                                    {channel.channelLabel}
                                                </Badge>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    icon="edit"
                                    aria-label={copy.editLabel(message.label)}
                                    onClick={() => setOpenKey(message.key)}
                                >
                                    {copy.edit}
                                </Button>
                            </div>

                            {message.key === 'appointment_reminder' && settings.data ? (
                                <SettingState
                                    label={copy.reminders}
                                    state={remindersState(settings.data)}
                                    anchor={REMINDERS_ID}
                                />
                            ) : null}

                            {message.key === 'report_ready_with_invoice' && settings.data ? (
                                <>
                                    <SettingState
                                        label={copy.invoiceWithReport}
                                        state={invoiceWithReportState(settings.data)}
                                        anchor={INVOICE_WITH_REPORT_ID}
                                    />
                                    <InvoiceMessageSetting settings={settings.data} />
                                </>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}

            {open ? (
                <ClientMessageEditor
                    key={open.key}
                    message={open}
                    description={copy.when[open.key] ?? ''}
                    companyName={companyName}
                    onClose={() => setOpenKey(null)}
                    {...(previewDelayMs === undefined ? {} : { previewDelayMs })}
                />
            ) : null}
        </Card>
    );
}

function remindersState(settings: TenantSettings): string {
    return settings.sendReminders
        ? copy.remindersOn(settings.reminderDaysBefore)
        : copy.remindersOff;
}

/** Dezelfde eerste zin als onder Automatisering, in dezelfde drie standen. */
function invoiceWithReportState(settings: TenantSettings): string {
    if (!settings.invoiceWithReport) {
        return copy.invoiceWithReportState.off;
    }

    return settings.autoSendReport
        ? copy.invoiceWithReportState.on
        : copy.invoiceWithReportState.manual;
}

/**
 * De stand van een schakelaar die onder Automatisering staat, met een link erheen.
 * De link opent het tabblad Rapport met de schakelaar in focus (`SettingsPage`).
 */
function SettingState({ label, state, anchor }: { label: string; state: string; anchor: string }) {
    return (
        <div
            role="group"
            aria-label={label}
            className="mt-[6px] rounded-[8px] bg-[var(--surface-subtle)] px-3 py-[8px]"
        >
            <p className="m-0 text-[13px] leading-[1.4] font-medium text-[var(--text-primary)]">
                {label}
            </p>
            <p className={`m-0 mt-[3px] ${SMALL}`}>{state}</p>
            <p className={`m-0 mt-[3px] ${SMALL}`}>
                {copy.setAtBefore}
                <a
                    href={`#${anchor}`}
                    className={cn(
                        'rounded-[2px] text-[var(--accent)] underline-offset-2 hover:text-[var(--accent-dark)] hover:underline',
                        focusRing,
                    )}
                >
                    {copy.setAtLink}
                </a>
                {copy.setAtAfter}
            </p>
        </div>
    );
}
